/** @jsx jsx */
/* eslint-disable jsx-a11y/label-has-associated-control, max-len */
import React, { useEffect, useState } from 'react';
import { jsx } from '@emotion/core';
import Modal from 'components/molecules/Modal';
import { getSupportCandidates } from 'api/shiftBoard';
import { OrgSimple, SupportCandidate } from 'domain/attend/shiftBoard/types';
import { boardStyles } from './styles';
import BoardSelect from './BoardSelect';

export type SupportStaffModalProps = {
  open: boolean;
  orgCode: string;
  supportOrgs: Array<OrgSimple>;
  targetDate: string;
  onClose: () => void;
  onAdd: (staffCodes: Array<string>) => void;
};

/**
 * 支援スタッフをシフト表に追加（他店のスタッフを行として出す。シフト登録はセルで行う）
 */
const SupportStaffModal: React.FC<SupportStaffModalProps> = ({
  open, orgCode, supportOrgs, targetDate, onClose, onAdd,
}) => {
  const [sourceOrg, setSourceOrg] = useState<string>('');
  const [candidates, setCandidates] = useState<Array<SupportCandidate>>([]);
  const [selected, setSelected] = useState<Array<string>>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setSourceOrg(supportOrgs.length > 0 ? supportOrgs[0].orgCode : '');
      setSelected([]);
      setCandidates([]);
    }
  }, [open, supportOrgs]);

  useEffect(() => {
    if (!open || !sourceOrg) {
      return;
    }
    setBusy(true);
    getSupportCandidates(orgCode, sourceOrg, targetDate)
      .then(setCandidates)
      .catch(() => setCandidates([]))
      .finally(() => setBusy(false));
  }, [open, orgCode, sourceOrg, targetDate]);

  return (
    <Modal open={open} closeHandler={onClose} title="支援スタッフを追加" submitText="シフト表に追加" onSubmit={() => onAdd(selected)} isDisabledSubmit={selected.length === 0} width="520px">
      <div css={boardStyles.modalBody}>
        <div css={boardStyles.formRow}>
          <label htmlFor="sup-org">支援元の組織</label>
          {/* ほかの画面と同じ共通の Select（10/7） */}
          <BoardSelect
            id="sup-org"
            value={sourceOrg}
            onChange={setSourceOrg}
            options={supportOrgs.map((o) => ({ value: o.orgCode, label: o.orgName }))}
            style={{ flex: 1 }}
          />
        </div>
        {busy && <div css={boardStyles.subText}>読み込み中…</div>}
        {/* 候補が少ないときも高さを残す。支援元の組織の一覧（共通の Select）が本文の枠で切れないように（10/7） */}
        <div style={{ minHeight: '120px', maxHeight: '300px', overflowY: 'auto' }}>
          {candidates.map((c) => (
            <label key={c.staffCode} htmlFor={`sup-${c.staffCode}`} css={boardStyles.listRow}>
              <input
                id={`sup-${c.staffCode}`}
                type="checkbox"
                checked={selected.indexOf(c.staffCode) !== -1}
                onChange={(e) => setSelected(e.target.checked ? [...selected, c.staffCode] : selected.filter((s) => s !== c.staffCode))}
              />
              <span style={{ fontWeight: 600 }}>{c.staffName}</span>
              {c.executiveName && <span css={boardStyles.subText}>{c.executiveName}</span>}
              {c.hasShiftOnDate && <span css={boardStyles.warnText}>当日シフトあり</span>}
            </label>
          ))}
          {!busy && candidates.length === 0 && <div css={boardStyles.subText}>候補がいません。</div>}
        </div>
      </div>
    </Modal>
  );
};

export default SupportStaffModal;
